import { useLazyQuery, useQuery } from '@apollo/client'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { LotContext } from '../../context/LotContext/lot.context'
import { GET_LOT_IMAGES_WITH_ID } from '../../graphql/queries/getLotImagesWithId.query'
import useBreakpoint from '../../hooks/useBreakpoint'

// routes
import { lotRoute } from '../../views/Lot/route'
import LoadingAnimation from '../LoadingAnimation/LoadingAnimation.component'

// style components
import {
    Container,
    DataContainer,
    DisabledText,
    LoadingAnimationStyle,
    LotAddress,
    LotImage,
    LotImageContainer,
    LotTitle
} from './LotCard.style'

// types
import {
    BookingGetLotImageUrlsQuery,
    BookingGetLotImageUrlsQueryVariables,
    BookingGetLotQuery,
    BookingGetLotQueryVariables
} from 'parkdepot-shared/gql/graphql'
import { GET_LOT_WITH_ID } from '../../graphql/queries/getLotWithId.query'
import { Lot } from '../../types/lot.interface'

interface LotCardProps {
    lot: Lot
    smallImage?: boolean
}

const LotCard: FC<LotCardProps> = ({ lot, smallImage = false }) => {
    const [lotAdditionalInfo, setLotAdditionalInfo] = useState<Lot | null>(null)
    const [lotImages, setLotImages] = useState<string[]>([])
    const history = useHistory()
    const { modifyAndSetLot } = useContext(LotContext)
    const { t } = useTranslation()
    const breakpoint = useBreakpoint()
    const isMobile = breakpoint <= 1

    const { data: lotData, loading } = useQuery<BookingGetLotImageUrlsQuery, BookingGetLotImageUrlsQueryVariables>(
        GET_LOT_IMAGES_WITH_ID,
        {
            variables: {
                lot_id: lot.id
            },
            fetchPolicy: 'no-cache'
        }
    )

    const [getLotWithId] = useLazyQuery<BookingGetLotQuery, BookingGetLotQueryVariables>(GET_LOT_WITH_ID)

    useEffect(() => {
        if (lotData && lotData.booking_get_lot_image_urls && lotData.booking_get_lot_image_urls.primary_url) {
            setLotImages([
                ...[lotData.booking_get_lot_image_urls.primary_url],
                ...(lotData.booking_get_lot_image_urls.secondary_urls as string[])
            ])
        }
    }, [lotData])

    useEffect(() => {
        const getNewLot = async () => {
            const newLotData = await getLotWithId({
                variables: {
                    lot_id: lot.id
                }
            })
            const newLot = newLotData?.data?.booking_get_lot as unknown as Lot
            const address = newLot.address.split(', ')
            setLotAdditionalInfo({
                ...newLot,
                // conditionally add address and city
                ...(address[0] && { address: address[0] }),
                ...(address[1] && { city: address[1] })
            })
        }
        // check if lot has certain properties, otherwise fetch it
        if (!lot.opening_hours || !lot.address) {
            getNewLot()
        }
    }, [getLotWithId, lot])

    const onCardClick = (e: { preventDefault: () => void }) => {
        e.preventDefault()
        if (lot.disabled) return
        modifyAndSetLot({
            ...(lotAdditionalInfo ? lotAdditionalInfo : lot),
            image_urls: lotImages,
            banner: lotImages[0]
        })
        history.push(lotRoute(lot.id.toLowerCase()))
    }

    return (
        <Container
            // disabled lots shouldn't be clickable and pretend to be a link
            $as={lot.disabled ? 'div' : 'a'}
            {...(lot.disabled ? {} : { href: lotRoute(lot.id.toLowerCase()) })}
            disabled={lot.disabled ?? true}
            aria-disabled={lot.disabled ?? true}
            onClick={onCardClick}
            data-testid="lot-card"
        >
            <LotImageContainer>
                {loading ? (
                    <LoadingAnimation setDefaultSize={false} style={LoadingAnimationStyle(isMobile, smallImage)} />
                ) : (
                    <>
                        <LotImage
                            loading="lazy"
                            disabled={lot.disabled}
                            $small={smallImage}
                            src={lotImages[0]}
                            alt={lot.name || lotAdditionalInfo?.name}
                        />
                        {lot.disabled && <DisabledText>{t('common.closedToday')}</DisabledText>}
                    </>
                )}
            </LotImageContainer>
            <DataContainer>
                <header>
                    <LotTitle>{lot.name || lotAdditionalInfo?.name}</LotTitle>
                </header>
                <address>
                    <LotAddress>{lot.address || lotAdditionalInfo?.address}</LotAddress>
                    <LotAddress>{lot.city || lotAdditionalInfo?.city}</LotAddress>
                </address>
            </DataContainer>
        </Container>
    )
}

export default React.memo(LotCard)
